import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { getPublishedOutlook } from "@/lib/weekly-outlook.functions";
import { WeeklyOutlookView } from "@/components/weekly-outlook/WeeklyOutlookView";

export const Route = createFileRoute("/weekly-outlook/$slug")({
  loader: async ({ params }) => {
    const o = await getPublishedOutlook({ data: { slug: params.slug } });
    if (!o) throw notFound();
    return o;
  },
  head: ({ loaderData, params }) => {
    const url = `https://xauproacademy.com/weekly-outlook/${params.slug}`;
    const title = loaderData ? `${loaderData.title} | XAU Pro Academy` : "Gold Weekly Outlook | XAU Pro Academy";
    const desc = loaderData?.summary ?? "Weekly XAUUSD review and outlook.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: loaderData?.title ?? "Gold Weekly Outlook" },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: loaderData?.title ?? "Gold Weekly Outlook" },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "AnalysisNewsArticle",
                headline: loaderData.title,
                description: desc,
                datePublished: loaderData.published_at,
                dateModified: loaderData.published_at,
                url,
                mainEntityOfPage: { "@type": "WebPage", "@id": url },
                inLanguage: "en",
                about: { "@type": "Thing", name: "XAUUSD gold price" },
                isPartOf: { "@type": "CollectionPage", name: "Gold Weekly Outlook Archive", url: "https://xauproacademy.com/weekly-outlook" },
                author: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
                publisher: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
                  { "@type": "ListItem", position: 2, name: "Weekly Outlook", item: "https://xauproacademy.com/weekly-outlook" },
                  { "@type": "ListItem", position: 3, name: loaderData.title, item: url },
                ],
              }),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-muted-foreground">Report not found.</p>
        <Link to="/weekly-outlook" className="mt-4 inline-block text-gold underline">All weekly reports</Link>
      </div>
    </Layout>
  ),
  component: Page,
});

function Page() {
  const o = Route.useLoaderData();
  return (
    <Layout>
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <Link to="/weekly-outlook" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-gold">All weekly reports</Link>
        <div className="mt-4"><WeeklyOutlookView o={o} /></div>
      </div>
    </Layout>
  );
}
